import React, { useState, useEffect } from 'react';
import { getLegacyClaimCorrespondence, LegacyCorrespondence, addLegacyClaimCorrespondence } from '../api/legacy';
import EmailViewer from './EmailViewer';
import { EmailCorrespondence } from '../api/claims';
import CallRecorderModal from './CallRecorderModal';
import { CallData } from './CallRecorder';
import './LegacyClaimCorrespondence.css';

interface LegacyClaimCorrespondenceProps {
  claimId: string;
  onCorrespondenceCountChange?: (count: number) => void;
}

export default function LegacyClaimCorrespondence({ claimId, onCorrespondenceCountChange }: LegacyClaimCorrespondenceProps) {
  const [correspondence, setCorrespondence] = useState<LegacyCorrespondence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailCorrespondence | null>(null);
  const [showCallRecorder, setShowCallRecorder] = useState(false);

  // Get user's full name from JWT token
  const getUserFullName = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Try different possible fields for the user's name
        if (payload.name) return payload.name;
        if (payload.fullName) return payload.fullName;
        if (payload.firstName && payload.lastName) {
          return `${payload.firstName} ${payload.lastName}`;
        }
        if (payload.firstName) return payload.firstName;
        if (payload.email) return payload.email;
        return 'Agent';
      } catch {
        return 'Agent';
      }
    }
    return 'Agent';
  };
  
  const currentUserName = getUserFullName();

  // Load correspondence when component mounts or claimId changes
  useEffect(() => {
    loadCorrespondence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const loadCorrespondence = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLegacyClaimCorrespondence(claimId);
      setCorrespondence(data);
      onCorrespondenceCountChange?.(data.length);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load correspondence');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Unknown date';
      return date.toLocaleString();
    } catch {
      return 'Unknown date';
    }
  };

  const getCorrespondenceIcon = (type: string) => {
    switch (type) {
      case 'email':
        return '📧';
      case 'letter':
        return '📄';
      case 'phone':
        return '📞';
      case 'fax':
        return '📠';
      case 'document':
        return '📎';
      default:
        return '📋';
    }
  };

  // Convert LegacyCorrespondence to EmailCorrespondence for the viewer
  const convertToEmailCorrespondence = (item: LegacyCorrespondence): EmailCorrespondence => {
    // Map legacy directions to email directions
    const emailDirection = (item.direction === 'sent' || item.direction === 'outgoing') ? 'sent' : 'received';
    
    return {
      id: item.id,
      type: 'email',
      direction: emailDirection,
      subject: item.subject || '(No subject)',
      from: item.sender || 'Unknown',
      to: item.recipient ? [item.recipient] : [],
      cc: [],
      timestamp: item.date,
      bodyPreview: item.content?.substring(0, 200) || '',
      body: item.content,
      hasAttachments: item.hasAttachments || false
    };
  };

  const handleSaveCall = async (callData: CallData) => {
    try {
      const newCorrespondence: Partial<LegacyCorrespondence> = {
        type: 'phone',
        direction: callData.direction as any, // Map incoming/outgoing to the legacy format
        subject: callData.subject,
        sender: callData.from,
        recipient: callData.to,
        date: new Date().toISOString(),
        content: callData.callNotes,
        callDuration: callData.callDuration,
        callAnswered: callData.callAnswered,
        callNotes: callData.callNotes
      };

      await addLegacyClaimCorrespondence(claimId, newCorrespondence);
      loadCorrespondence(); // Reload to show the new call record
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save call record');
    }
  };

  return (
    <div className="legacy-correspondence-container">
      <div className="correspondence-header">
        <h3>Correspondence History</h3>
        <div className="correspondence-actions">
          <button 
            className="btn-primary record-call-btn" 
            onClick={() => setShowCallRecorder(true)}
          >
            📞 Record Call
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading correspondence...</div>
      ) : correspondence.length === 0 ? (
        <div className="no-correspondence">No correspondence found for this claim</div>
      ) : (
        <div className="correspondence-list">
          {correspondence.map((item) => (
            <div 
              key={item.id} 
              className={`correspondence-item ${item.type === 'email' ? 'clickable' : ''}`}
              onClick={() => {
                if (item.type === 'email' && item.content) {
                  setSelectedEmail(convertToEmailCorrespondence(item));
                }
              }}
            >
              <div className="correspondence-icon">{getCorrespondenceIcon(item.type)}</div>
              <div className="correspondence-details">
                <div className="correspondence-subject">
                  {item.subject || `${item.type.charAt(0).toUpperCase() + item.type.slice(1)} Correspondence`}
                </div>
                <div className="correspondence-meta">
                  <span className="correspondence-type">{item.type.toUpperCase()}</span>
                  <span>•</span>
                  <span>{item.direction === 'sent' || item.direction === 'outgoing' ? `To: ${item.recipient}` : `From: ${item.sender}`}</span>
                  <span>•</span>
                  <span>{formatDate(item.date)}</span>
                  {item.type === 'phone' && item.callDuration && (
                    <>
                      <span>•</span>
                      <span className="call-duration">
                        Duration: {Math.floor(item.callDuration / 60)}:{(item.callDuration % 60).toString().padStart(2, '0')}
                      </span>
                    </>
                  )}
                  {item.type === 'phone' && (
                    <>
                      <span>•</span>
                      <span className={`call-status ${item.callAnswered ? 'answered' : 'missed'}`}>
                        {item.callAnswered ? '✓ Answered' : '✗ Missed'}
                      </span>
                    </>
                  )}
                  {item.hasAttachments && (
                    <span className="has-attachments">📎 Attachments</span>
                  )}
                </div>
                {item.content && (
                  <div className="correspondence-preview">
                    {item.content.substring(0, 150)}...
                  </div>
                )}
              </div>
              <span className={`correspondence-direction ${item.direction}`}>
                {item.direction === 'sent' || item.direction === 'outgoing' ? '↗️ Outgoing' : 
                 item.direction === 'received' || item.direction === 'incoming' ? '↘️ Incoming' : 
                 item.direction}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Email Viewer Modal */}
      {selectedEmail && (
        <EmailViewer
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}

      {/* Call Recorder Modal */}
      <CallRecorderModal
        isOpen={showCallRecorder}
        onClose={() => setShowCallRecorder(false)}
        onSave={handleSaveCall}
        initialFrom={currentUserName}
        initialTo={'Legacy Claimant'}
      />
    </div>
  );
}